import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';


import {AccountInformation} from 'src/components/account-information';
import {
    NO_ACCOUNT_NAME,
    NO_ACCOUNT_TEMPLATE_USER,
    NO_COUNTRY,
    VALIDATION_ERRORS,
} from 'src/components/account-information/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    BASIC_VALIDATION_FAILED,
    NO_PRIMARY_ATM,
    NO_PRIMARY_ATM_MESSAGE,
    NO_PRIMARY_ME_IN_TEAM,
    NO_PRIMARY_ME_IN_TEAM_MESSAGE,
} from 'src/modules/create-account/constants';
import {CreateAccountStep} from 'src/modules/create-account/create-account-step';
import {CREATE_ACCOUNT_STEPS} from 'src/modules/create-account/steps';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {saveAccountManagerRelations} from 'src/openapi/admin/apis/AccountManagerRelationsApi';
import {createAccount} from 'src/openapi/admin/apis/CreateAccountApi';
import {getManagedAccounts} from 'src/openapi/admin/apis/GetManagedAccountsApi';
import {updateAccount} from 'src/openapi/admin/apis/UpdateAccountApi';
import {updateAccountCrmId} from 'src/openapi/admin/apis/UpdateAccountCrmIdApi';
import {updateAccountPbIds} from 'src/openapi/admin/apis/UpdateAccountPbIdsApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';

import {AccountTeam} from './components/account-team';

import type {AccountDetails, AccountTeamMember} from 'src/modules/create-account/typedef';

type Props = {
    onNext: () => void,
    setAccountId: React.Dispatch<React.SetStateAction<number | undefined>>,
    setAccountEntityType: React.Dispatch<React.SetStateAction<string>>,
    accountId?: number,
};

export const DEFAULT_DETAILS: AccountDetails = {
    accountName: '',
    countryCode: 'US',
    statusCode: 'Client',
    entityTypeCode: 'Client Account',
    accountTeamMembers: [{primary: true, type: 'AM', receiveAccountAlerts: true}],
    pbIds: [''],
};

export const CreateAccountDetails = (props: Props) => {
    const {onNext, setAccountId, setAccountEntityType, accountId} = props;
    const me = useSelector(selectUserShortInfo);
    const apiDispatch = useAppDispatch();

    const {canCreateAccount, hasAccess} = useAdminAccessRight();
    const {accountCreateSubmit, accountFieldChange} = useEventTracking();

    const [accountDetails, setAccountDetails] = useState<AccountDetails>(DEFAULT_DETAILS);
    const [initialAccountDetails, setInitialAccountDetails] = useState<AccountDetails>(DEFAULT_DETAILS);
    const [busy, setBusy] = useState(false);

    const {state} = useLocation();
    const prevPath = state?.prevPath;
    const navigate = useNavigate();

    const {addError, validationErrors} = useContext(ValidationContext);
    const {setErrorMessage} = useContext(MessageContext);
    const {setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'account-details',
        onProceed: () => {
            onNext();
            if (me) {
                apiDispatch(getManagedAccounts({accountManagerId: me.userId}));
            }
        },
    });

    useEffect(() => {
        const changed = !isDeepEqual({current: accountDetails, prev: initialAccountDetails});

        setIsChunkChanged(changed);
    }, [accountDetails, initialAccountDetails, setIsChunkChanged]);


    const onLoadAccountInformation = useCallback(({accountTeamMembers, ...information}: AccountDetails) => {
        setAccountDetails(details => ({...details, ...information}));
        setInitialAccountDetails(details => ({...details, ...information}));
    }, []);

    const onLoadAccountTeam = useCallback((accountTeam: Array<AccountTeamMember>) => {
        setAccountDetails(details => ({
            ...details,
            accountTeamMembers: accountTeam,
        }));
        setInitialAccountDetails(details => ({
            ...details,
            accountTeamMembers: accountTeam,
        }));
    }, []);

    const onFieldChange = useCallback((parameterName: string) => {
        accountFieldChange({
            section: 'Account Details',
            parameterName,
            viewType: 'New',
        });
    }, [accountFieldChange]);

    const checkValidationErrors = useCallback(() => {
        let hasErrors = false;

        if (!accountDetails.accountName) {
            addError({
                code: NO_ACCOUNT_NAME,
                message: VALIDATION_ERRORS.NO_ACCOUNT_NAME,
                controlId: 'account-name',
            });
            hasErrors = true;
        }

        if (!accountDetails.templateUser) {
            addError({
                code: NO_ACCOUNT_TEMPLATE_USER,
                message: VALIDATION_ERRORS.NO_ACCOUNT_TEMPLATE_USER,
                controlId: 'account-template-user',
            });
            hasErrors = true;
        }

        if (!accountDetails.countryCode) {
            addError({code: NO_COUNTRY, message: VALIDATION_ERRORS.NO_COUNTRY, controlId: 'account-country-select'});
            hasErrors = true;
        }

        accountDetails.accountTeamMembers.forEach(({primary, user}, id) => {
            if (primary && !user) {
                addError({
                    code: NO_PRIMARY_ATM,
                    message: NO_PRIMARY_ATM_MESSAGE,
                    controlId: `account-team-member-select-${id}`,
                });
                hasErrors = true;
            }
        });

        if (
            !hasAccess(ACCESS_RIGHTS.GLOBAL_ACCOUNT_MANAGEMENT) &&
            !accountDetails.accountTeamMembers.some(({user}) => user?.id === me?.userId)
        ) {
            addError({
                code: NO_PRIMARY_ME_IN_TEAM,
                message: NO_PRIMARY_ME_IN_TEAM_MESSAGE,
                controlId: 'account-team',
            });
            hasErrors = true;
        }

        return hasErrors || !!validationErrors.length;
    }, [accountDetails, validationErrors, me, addError, hasAccess]);

    const onSaveAndContinue = useCallback(async () => {
        if (accountId && isDeepEqual({prev: initialAccountDetails, current: accountDetails})) {
            onNext();
            return;
        }

        const {accountName, countryCode, statusCode, entityTypeCode, templateUser} = accountDetails;
        const populatedTeamMembers = accountDetails.accountTeamMembers.filter(({user}) => user);
        let responseErrors: Array<string> = [];
        const hasValidationErrors = checkValidationErrors();

        if (hasValidationErrors) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            return;
        }

        if (accountName && countryCode && statusCode && entityTypeCode && templateUser &&
            populatedTeamMembers.length
        ) {
            setBusy(true);
            const accountUpdateRequest = {
                accountName,
                countryCode,
                entityTypeCode,
                statusCode,
                templateUserId: templateUser.userId,
                parentAccountId: accountDetails.parentAccount?.accountId || undefined,
            };
            const accountResponse = accountId
                ? await apiDispatch(updateAccount({accountUpdateRequest, accountId}))
                : await apiDispatch(createAccount({accountUpdateRequest}));

            if (accountResponse.payload.success) {
                const processedAccountId = accountId || accountResponse.payload.data || 0;

                const updateATMRelationsResponse = await apiDispatch(saveAccountManagerRelations({
                    accountId: processedAccountId,
                    saveManagerRelationsRequest: {
                        relations: populatedTeamMembers.map(teamMember => ({
                            accountManagerId: teamMember.user?.id,
                            primary: teamMember.primary,
                            receiveAlert: teamMember.receiveAccountAlerts,
                            typeCode: teamMember.type,
                        })),
                    },
                }));

                if (updateATMRelationsResponse.payload.error) {
                    responseErrors = responseErrors.concat(
                        getError({error: updateATMRelationsResponse.payload.data, separator: ' '}),
                    );
                }

                const updateSFDCIDResponse = await apiDispatch(updateAccountCrmId({
                    accountId: processedAccountId,
                    body: accountDetails.sfdcId || '',
                }));

                if (updateSFDCIDResponse.payload.error) {
                    responseErrors = responseErrors.concat(
                        getError({error: updateSFDCIDResponse.payload.data, separator: ' '}),
                    );
                }

                const updatePBIDsResponse = await apiDispatch(updateAccountPbIds({
                    accountId: processedAccountId,
                    requestBody: accountDetails.pbIds.filter(Boolean),
                }));

                if (updatePBIDsResponse.payload.error) {
                    responseErrors = responseErrors.concat(
                        getError({error: updatePBIDsResponse.payload.data, separator: ' '}),
                    );
                }

                setAccountId(processedAccountId);
                setAccountEntityType(entityTypeCode);
            } else {
                responseErrors = responseErrors.concat(getError({
                    error: accountResponse.payload.data,
                    separator: ' ',
                }));
            }

            setBusy(false);
            if (!responseErrors.length) {
                setInitialAccountDetails(accountDetails);
                proceed();
            } else {
                setErrorMessage(responseErrors.join('\n'));
            }
        }
    }, [
        accountId,
        accountDetails,
        initialAccountDetails,
        proceed,
        apiDispatch,
        checkValidationErrors,
        onNext,
        setAccountId,
        setAccountEntityType,
        setErrorMessage,
    ]);

    const onCancel = useCallback(() => {
        accountCreateSubmit({
            place: 'Account Details',
            action: 'Cancel',
        });
        if (prevPath) {
            navigate(-1);
        } else {
            navigate('/');
        }
    }, [accountCreateSubmit, prevPath, navigate]);

    if (!canCreateAccount) {
        return (
            <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to Create New Account page"/>
        );
    }

    return (
        <CreateAccountStep {...{
            selectedStep: CREATE_ACCOUNT_STEPS.ACCOUNT_DETAILS,
            content: [
                <AccountInformation
                    key="account-information"
                    {...{
                        accountId,
                        accountDetails,
                        onLoad: onLoadAccountInformation,
                        onChange: setAccountDetails,
                        onFieldChange,
                    }}
                />,
                <AccountTeam
                    key="account-team"
                    {...{
                        accountId,
                        accountTeamMembers: accountDetails.accountTeamMembers,
                        onLoad: onLoadAccountTeam,
                        onFieldChange,
                        onChange: setAccountDetails,
                    }}
                />,
            ],
            buttonsBlock: [
                <Button key="Cancel" variant="Type.SECONDARY" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="Save"
                    variant="Type.PRIMARY"
                    busy={busy}
                    onClick={onSaveAndContinue}>
                    {accountId ? 'Save & Continue' : 'Create'}
                </Button>,
            ],
        }}
        />
    );
};
